import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { CookieAuth } from '@highlander/common/helpers/authentication';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { PermissionTypes, UserPermission } from '../authorization/permissions/permissions.model';
import { Identity, mapClaimsIdentityToIdentity, MappedUserModel, UserModel } from './identity.models';
import { IdentityService } from './identity.service';

// Define the store state structure
export type IdentityState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  isDataLoaded: boolean;
  user: MappedUserModel | null;
};

// Initial state values
const initialState: IdentityState = {
  isAuthenticated: false,
  isLoading: false,
  isDataLoaded: false,
  user: null,
};

// Create the signal store
export const IdentityStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('identity-store'),
  withComputed(state => ({
    // Computed properties for frequently accessed values
    currentUser: computed(() => state.user()?.userinfo || null),
    currentUserId: computed(() => state.user()?.userinfo.id || null),
    userPermissions: computed(() => state.user()?.permissions || []),
    expiresAt: computed(() => state.user()?.expires_at || 0),
    currentUserAgreements: computed(() => state.user()?.userinfo?.agreements || []),
    // Backwards compatible methods required for Projects
    isTr: computed(() => state.user()?.userinfo?.isTr || false),
    isMapped: computed(() => state.user()?.userinfo?.isMapped || false),
    partnerId: computed(() => state.user()?.userinfo?.partnerId),
  })),
  withMethods((store, identityService = inject(IdentityService), router = inject(Router)) => {
    const utilityMethods = {
      hasPermission: (permission: PermissionTypes): boolean => {
        const permissions = store.userPermissions();
        return permissions.some(p => p.name === permission);
      },

      hasAnyPermission: (permissions: PermissionTypes[]): boolean => {
        const userPermissions = store.userPermissions();
        return permissions.some(permission => userPermissions.some(p => p.name === permission));
      },

      hasPermissionSignal: (permission: PermissionTypes): Signal<boolean> => {
        return computed(() => {
          const permissions = store.userPermissions();
          return permissions.some(p => p.name === permission);
        });
      },
    };
    return {
      // Check authentication status
      checkAuth: rxMethod<void>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true });
          }),
          switchMap(() =>
            identityService.checkAuth().pipe(
              tap(({ userinfo, permissions, expires_at }: UserModel) => {
                patchState(store, {
                  user: {
                    userinfo: mapClaimsIdentityToIdentity(userinfo),
                    permissions,
                    expires_at,
                  },
                  isAuthenticated: true,
                  isDataLoaded: true,
                  isLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, {
                  isAuthenticated: false,
                  isLoading: false,
                  isDataLoaded: true,
                  user: null,
                });
                CookieAuth.login();
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      silentCheckAuth: rxMethod<void>(
        pipe(
          switchMap(() =>
            identityService.checkAuth().pipe(
              tap(({ userinfo, permissions, expires_at }: UserModel) => {
                patchState(store, {
                  user: {
                    userinfo: mapClaimsIdentityToIdentity(userinfo),
                    permissions,
                    expires_at,
                  },
                  isAuthenticated: true,
                  isDataLoaded: true,
                });
              }),
              catchError(() => {
                patchState(store, {
                  isAuthenticated: false,
                  isDataLoaded: true,
                  user: null,
                });
                CookieAuth.login();
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      // Refresh authentication token
      refresh: rxMethod<void>(
        pipe(
          switchMap(() =>
            identityService.refresh().pipe(
              tap(({ expires_at }) => {
                const currentUser = store.user();
                if (currentUser) {
                  patchState(store, {
                    user: { ...currentUser, expires_at },
                    isAuthenticated: true,
                  });
                }
              }),
              catchError(() => {
                CookieAuth.logout();
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      login: () => {
        // Handle user login
        CookieAuth.login();
        patchState(store, initialState);
      },
      // Handle user logout
      logout: () => {
        CookieAuth.logout();
        patchState(store, initialState);
      },

      ...utilityMethods,
    };
  }),
);

// TypeScript helper to precisely define the IdentityStore type
// This creates a union type of all signals and methods defined in the store
export interface IdentityStoreType {
  // State properties (from withState)
  readonly isAuthenticated: Signal<boolean>;
  readonly isLoading: Signal<boolean>;
  readonly isDataLoaded: Signal<boolean>;
  readonly user: Signal<MappedUserModel | null>;

  // Computed properties (from withComputed)
  readonly currentUser: Signal<Identity | null>;
  readonly currentUserId: Signal<string | null>;
  readonly userPermissions: Signal<UserPermission[]>;
  readonly expiresAt: Signal<number>;
  readonly isTr: Signal<boolean>;
  readonly isMapped: Signal<boolean>;
  readonly partnerId: Signal<string | undefined>;
  readonly currentUserAgreements: Signal<string[]>;

  // Methods (from withMethods)
  readonly checkAuth: () => void;
  readonly silentCheckAuth: () => void;
  readonly refresh: () => void;
  readonly logout: () => void;
  readonly login: () => void;

  readonly hasPermission: (permission: PermissionTypes) => boolean;
  readonly hasAnyPermission: (permissions: PermissionTypes[]) => boolean;
  readonly hasPermissionSignal: (permission: PermissionTypes) => Signal<boolean>;
}
